import { useEffect, useMemo } from 'react';
import { useIntl } from '@/components/formatted_message/FormattedMessage';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import Toast from '@/components/Toast';
import { AuthenticatedApp } from '@/containers/AuthenticatedApp';
import { useGaTracker } from '@/hooks/google_analytics';
import GlobalStyle from '../global-styles';
import { Root as messages } from './messages';
import { MOBILE_SIZE } from '@/containers/AuthenticatedApp/constants';
import useWindowDimensions from '@/utils/hooks/useWindowDimensions';
import { AuthPage } from '@/routes/auth/Page';
import { setRequestIntlInstance } from '@/utils/request';
import { usePlatform } from '@/hooks/usePlatform';
import { changeIsMobile } from './AuthenticatedApp/actions';
import { useAuthContext } from '@/contexts/AuthContext';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMsClarity } from '@/hooks';

function Root() {
  const intl = useIntl();
  useMsClarity();
  useGaTracker();
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useAuthContext();

  const { width } = useWindowDimensions();
  const { isMobile, setIsMobile } = usePlatform();

  // Show global loader on specific authentication related loading states. This is to prevent showing incomplete app content when the user is being logged out or when the session is being restored.
  const shouldShowGlobalLoader =
    loading.isLoadingLogout ||
    loading.isLoadingLoginToCustomer ||
    loading.isLoadingLogoutFromCustomer ||
    loading.isLoadingRestoreSession;

  useGlobalLoader(shouldShowGlobalLoader);

  useEffect(() => {
    if (isAuthenticated) return;

    const path = window.location.pathname + window.location.search;

    const authRoutes = [
      '/forgot-password',
      '/one-time-password',
      '/set-password',
      '/login',
      '/two-factor',
      '/lost-device',
      '/consent',
      'token',
    ];

    const isAuthRoute = authRoutes.some(route => path.includes(route));
    const isRootPath = path === '/';

    if (!(isAuthRoute || isRootPath)) {
      sessionStorage.setItem('lastAttemptedPath', path);
    }
  }, [isAuthenticated]);

  // Handles switching to mobile state
  useEffect(() => {
    if (!isMobile && width < MOBILE_SIZE) {
      setIsMobile(true);
      // leftover due to backwards compatibility
      dispatch(changeIsMobile(true));
    }
    if (isMobile && width > MOBILE_SIZE) {
      setIsMobile(false);
      // leftover due to backwards compatibility
      dispatch(changeIsMobile(false));
    }
  }, [width, isMobile]);

  // Instance of intl is used to translate notification message for 403 error related to permissions inside request function (src/utils/request.ts)
  useEffect(() => {
    setRequestIntlInstance(intl);
  }, [intl]);

  const isSettingPassword = window.location.pathname.startsWith('/token/set-password');
  const isConsent = window.location.pathname.startsWith('/consent');

  const appContent = useMemo(() => {
    if (!isAuthenticated || isSettingPassword || isConsent) {
      return <AuthPage />;
    }
    return <AuthenticatedApp />;
  }, [isAuthenticated, isSettingPassword, isConsent]);

  return (
    <>
      <Helmet
        titleTemplate={`%s | ${intl.formatMessage(messages.title)}`}
        defaultTitle={intl.formatMessage(messages.title)}
      />
      {appContent}
      <Toast />
      <GlobalStyle />
    </>
  );
}

export default Root;
